import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useSearchParams } from 'react-router-dom';
import FilterTabs, { type TabFilter } from '../../components/portal/FilterTabs';
import EmailListItem from '../../components/portal/EmailListItem';
import EmailViewModal from '../../components/portal/EmailViewModal';
import EmailComposeModal from '../../components/portal/EmailComposeModal';
import EditProposalReplySettlePrompt from '../../components/portal/EditProposalReplySettlePrompt';
import { proposalToSettleAfterReply } from '../../utils/editProposalReplySettle';
import type { PortalEmailEditProposal } from '../../types/portal';
import AssignModal from '../../components/portal/AssignModal';
import ImportantSubFilter, { type ImportantReadState } from '../../components/portal/ImportantSubFilter';
import InboxBulkBar from '../../components/portal/InboxBulkBar';
import TrashGuardModal from '../../components/portal/TrashGuardModal';
import Toast from '../../components/portal/Toast';
import AdminViewAsPrompt from '../../components/portal/AdminViewAsPrompt';
import InboxAlertBanners from '../../components/portal/InboxAlertBanners';
import QualityAsksCard from '../../components/portal/qualityAsks/QualityAsksCard';
import InboxViewDropdowns from '../../components/portal/InboxViewDropdowns';
import type { InboxSortBy, InboxSortOrder } from '../../components/portal/InboxViewDropdowns';
import { portalService } from '../../services/portal';
import { trackEvent } from '../../services/portalTracking';
import { useListNavigation, type NavigationTarget } from '../../hooks/useListNavigation';
import type {
  PortalEmailItem,
  PortalInboxStats,
  InboxAlertsData,
  SalespersonOption,
} from '../../types/portal';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';
import { mapEmailToComponent } from '../../utils/mapEmailToComponent';
import {
  parseTrashGuardError,
  partitionGuarded,
  type GuardedTrashEmail,
} from '../../utils/trashGuard';
import { AI_DRAFT_POLL_MS, shouldPollForDrafting } from '../../utils/aiDrafting';
import { buildInboxTabs, resolveUnmappedCount, resolveAllImportantCount, filterInboxEmails, isEmailResolved } from './inboxTabUtils';
import {
  isInboxTab,
  readRememberedInboxTab,
  rememberInboxTab,
  resolveInitialInboxTab,
} from './inboxTabMemory';
import type { InboxTabSource, InitialInboxTab } from './inboxTabMemory';
import styles from './PortalInboxPage.module.css';

const PortalInboxPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const category = (searchParams.get('category') || 'all') as
    | 'all'
    | 'new-customers'
    | 'orders'
    | 'quotes'
    | 'inquiries'
    | 'internal';

  // API data state
  const [emails, setEmails] = useState<PortalEmailItem[]>([]);
  const [totalEmails, setTotalEmails] = useState<number>(0);
  const [stats, setStats] = useState<PortalInboxStats | null>(null);
  const [alertsData, setAlertsData] = useState<InboxAlertsData | null>(null);
  const [salespersons, setSalespersons] = useState<SalespersonOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Unmapped emails state (separate fetch with view=unmapped)
  const [unmappedEmails, setUnmappedEmails] = useState<PortalEmailItem[]>([]);
  const [totalUnmapped, setTotalUnmapped] = useState<number>(0);
  const [loadingUnmapped, setLoadingUnmapped] = useState(false);
  const unmappedLoadedRef = useRef(false);

  const PAGE_SIZE = 50;

  // UI state – check for deep-link emailId in query params.
  //
  // Do NOT seed selectedEmailId from deepLinkEmailId here. The deep-link
  // useEffect below short-circuits when selectedEmailId is already truthy
  // (`if (!deepLinkEmailId || selectedEmailId) return`), so seeding it
  // would prevent the cross-view fetch from running for any email that
  // isn't in the user's current "important" view — the email would never
  // be added to `emails`, `mappedSelected` would stay null, and the
  // EmailViewModal would never render. Let the effect own the lifecycle.
  const deepLinkEmailId = searchParams.get('emailId');
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  // Which tab the inbox opens on: an explicit ?tab= wins, then an email deep
  // link (My Important), then the tab this person last chose (remembered per
  // user, kanban 778fe5cf), then My Important. Resolved once, at mount; the
  // precedence rule and storage live in ./inboxTabMemory.ts.
  const userId = user?.id ?? null;
  const [initialTabState] = useState<InitialInboxTab>(() =>
    resolveInitialInboxTab({
      urlTab: searchParams.get('tab'),
      urlEmailId: deepLinkEmailId,
      remembered: readRememberedInboxTab(userId),
    }),
  );
  const [activeTab, setActiveTab] = useState<TabFilter>(initialTabState.tab);
  // How the current tab came to be active. Shown under the tabs while it is
  // restored, so a remembered tab is never mistaken for "the inbox always
  // opens like this"; becomes 'chosen' at the first click.
  const [tabSource, setTabSource] = useState<InboxTabSource | 'chosen'>(initialTabState.source);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeAction, setComposeAction] = useState<'reply' | 'reply_all' | 'forward'>('reply');
  // After a reply on an email that carries an OPEN Merlin change card: ask
  // whether the reply settled it (kanban 006f1c9e). null = nothing to ask.
  const [replySettle, setReplySettle] = useState<{
    proposal: PortalEmailEditProposal;
    replyText: string;
  } | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkTrashing, setBulkTrashing] = useState(false);
  // Server trash guard (409): guarded emails + the ids of the blocked request.
  const [trashGuard, setTrashGuard] = useState<{
    guarded: GuardedTrashEmail[];
    pendingIds: string[];
  } | null>(null);
  const [bulkResolving, setBulkResolving] = useState(false);
  const [sortBy, setSortBy] = useState<InboxSortBy>('received_at');
  const [sortOrder, setSortOrder] = useState<InboxSortOrder>('desc');
  const [showResolved, setShowResolved] = useState(false);
  // Read-state sub-filter for the Important tab.  'all' = no overlay,
  // 'unread' = UNREAD only, 'read' = !UNREAD only.
  const [importantReadState, setImportantReadState] = useState<ImportantReadState>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const { toasts, addToast, removeToast } = useToast();

  // Track search events when debounced value changes
  useEffect(() => {
    if (debouncedSearch) {
      trackEvent('search', 'inbox_search', { query: debouncedSearch });
    }
  }, [debouncedSearch]);

  // Close the email viewer and clear any deep-link param so it doesn't reopen
  const closeEmailViewer = useCallback(() => {
    setSelectedEmailId(null);
    if (searchParams.has('emailId')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('emailId');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCategoryChange = useCallback(
    (newCategory: string) => {
      trackEvent('filter', 'inbox_category', { category: newCategory });
      const next = new URLSearchParams(searchParams);
      if (newCategory === 'all') {
        next.delete('category');
      } else {
        next.set('category', newCategory);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Track loaded count via ref so fetchEmails doesn't depend on emails state
  const loadedCountRef = useRef(0);

  // Map active tab → backend `view` param.  The 'unmapped' tab uses a
  // separate fetcher (fetchUnmappedEmails); for everything else we let the
  // backend filter so the list-footer total matches the tab pill count.
  const viewForTab = useMemo<string>(() => {
    if (activeTab === 'all-important') return 'all-important';
    // 'important' = My Important; fall through covers any unexpected value
    // (defaults to the user's important emails).
    return 'important';
  }, [activeTab]);

  // Fetch emails from API (initial load replaces list, loadMore appends).
  // `silent` refreshes the list without the loading spinner — used by the
  // AI-drafting poll so badges transition without UI flicker.
  const fetchEmails = useCallback(
    async (loadMore = false, silent = false) => {
      try {
        if (loadMore) {
          setLoadingMore(true);
        } else if (!silent) {
          setLoading(true);
          setError(null);
        }
        const offset = loadMore ? loadedCountRef.current : 0;
        // Important tabs (My Important / All Important): layer the
        // read/unread sub-filter and showResolved overlay on top of the
        // chosen view.  Other tabs send no read_state — the view already
        // encodes intent.
        const isImportantTab =
          activeTab === 'important' || activeTab === 'all-important';
        const readStateParam =
          isImportantTab && importantReadState !== 'all'
            ? importantReadState
            : undefined;
        const response = await portalService.listInboxEmails({
          view: viewForTab,
          limit: PAGE_SIZE,
          offset,
          search: debouncedSearch || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          read_state: readStateParam,
          // show_resolved only meaningful for important views; passing it
          // on other views is a harmless no-op server-side.
          show_resolved: isImportantTab ? showResolved : undefined,
        });
        if (loadMore) {
          setEmails((prev) => {
            const next = [...prev, ...response.emails];
            loadedCountRef.current = next.length;
            return next;
          });
        } else {
          setEmails(response.emails);
          loadedCountRef.current = response.emails.length;
        }
        setTotalEmails(response.total);
      } catch (err) {
        console.error('Failed to fetch emails:', err);
        if (!loadMore) {
          setError('Failed to load emails. Please try again.');
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // Include user?.id to trigger refetch when impersonation changes.
    // activeTab/importantReadState/showResolved/viewForTab are part of the
    // query — without them, switching tabs would silently re-run the prior
    // tab's query and the footer total would lag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, sortBy, sortOrder, debouncedSearch, viewForTab, activeTab, importantReadState, showResolved],
  );

  const handleLoadMore = useCallback(() => {
    fetchEmails(true);
  }, [fetchEmails]);

  // Fetch stats from API
  const fetchStats = useCallback(async (opts?: { fresh?: boolean }) => {
    try {
      // After one of this page's own mutations the pills must reflect it at
      // once: drop the 5 s client memo AND have the server bypass its 30 s
      // one. The mount read (fresh: false) is content with the server memo —
      // a count at most 30 s old is fine on landing, and any mutation made
      // elsewhere already flagged the next read as fresh.
      if (opts?.fresh !== false) portalService.invalidateStatsCache();
      const statsData = await portalService.getInboxStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
    // Include user?.id to refresh stats when impersonation changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Fetch salespersons for assignment
  const fetchSalespersons = useCallback(async () => {
    try {
      const data = await portalService.listSalespersons();
      setSalespersons(data);
    } catch (err) {
      console.error('Failed to fetch salespersons:', err);
    }
  }, []);

  // Fetch alerts data (unmapped count for tab badge)
  const fetchAlerts = useCallback(async () => {
    try {
      const data = await portalService.getInboxAlerts();
      setAlertsData(data);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  }, []);

  // Fetch unmapped emails (separate API call with view=unmapped)
  const fetchUnmappedEmails = useCallback(async () => {
    try {
      setLoadingUnmapped(true);
      const response = await portalService.listInboxEmails({
        view: 'unmapped',
        limit: 100,
        offset: 0,
      });
      setUnmappedEmails(response.emails);
      setTotalUnmapped(response.total);
      unmappedLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to fetch unmapped emails:', err);
    } finally {
      setLoadingUnmapped(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchEmails(false);
    fetchStats({ fresh: false });
    fetchSalespersons();
    fetchAlerts();
  }, [fetchEmails, fetchStats, fetchSalespersons, fetchAlerts]);

  // While any visible email is still being drafted by the AI, silently
  // re-poll so the badge transitions drafting → drafted/failed without a
  // manual reload (and the new draft's order badge appears on its own).
  const pollForDrafting = shouldPollForDrafting(
    emails,
    loadedCountRef.current,
    PAGE_SIZE,
  );
  useEffect(() => {
    if (!pollForDrafting) return;
    const intervalId = window.setInterval(() => {
      fetchEmails(false, true);
    }, AI_DRAFT_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [pollForDrafting, fetchEmails]);

  // A restored tab is applied without a click, so record it under the same
  // event as a click with source=restored: the usage graph counts "opened on
  // Unmapped" either way and can still tell the two apart. The Unmapped tab
  // has its own fetcher, so an open that lands there (restored or ?tab=)
  // loads it the way a click would.
  useEffect(() => {
    if (initialTabState.source === 'restored') {
      trackEvent('filter', 'inbox_tab', { tab: initialTabState.tab, source: 'restored' });
    }
    if (initialTabState.tab === 'unmapped' && !unmappedLoadedRef.current) {
      fetchUnmappedEmails();
    }
    // Mount-only: initialTabState never changes after the first render.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset tab and selection when the category changes (but not when
  // deep-linked). Only an actual change counts: on mount the tab is whatever
  // resolveInitialInboxTab chose, and a remembered tab must survive it.
  const prevCategoryRef = useRef(category);
  useEffect(() => {
    if (prevCategoryRef.current === category) return;
    prevCategoryRef.current = category;
    setActiveTab('important');
    setTabSource('chosen');
    if (!deepLinkEmailId) {
      closeEmailViewer();
    }
  }, [category, deepLinkEmailId, closeEmailViewer]);

  // The Unmapped tab only exists while there are unmapped emails. If the
  // inbox opened on it (restored or deep-linked) and the fetch finds none,
  // or the last unmapped email is cleared from it, fall back to My Important
  // rather than sitting on a tab that is no longer drawn.
  useEffect(() => {
    if (activeTab !== 'unmapped' || loadingUnmapped || !unmappedLoadedRef.current) return;
    if (totalUnmapped === 0) {
      setActiveTab('important');
      setTabSource('default');
    }
  }, [activeTab, loadingUnmapped, totalUnmapped]);

  // Deep-link: auto-select the email once it appears in the loaded list.
  // If the email isn't in the current view, fetch it by ID and prepend.
  const deepLinkAttemptedRef = useRef(false);
  useEffect(() => {
    if (!deepLinkEmailId || selectedEmailId) return;
    if (emails.length === 0 && loading) return;

    const found = emails.find((e) => e.id === deepLinkEmailId);
    if (found) {
      setSelectedEmailId(deepLinkEmailId);
      return;
    }

    // Only attempt the cross-view fetch once
    if (deepLinkAttemptedRef.current) return;
    deepLinkAttemptedRef.current = true;

    // Email not in this view — fetch by ID directly (bypasses view filters)
    (async () => {
      try {
        const resp = await portalService.listInboxEmails({
          email_id: deepLinkEmailId,
          limit: 1,
        });
        if (resp.emails.length > 0) {
          setEmails((prev) => {
            if (prev.some((e) => e.id === deepLinkEmailId)) return prev;
            return [resp.emails[0], ...prev];
          });
          setSelectedEmailId(deepLinkEmailId);
        }
      } catch {
        // Silent fail — deep-link just won't auto-select
      }
    })();
  }, [deepLinkEmailId, emails, selectedEmailId, loading]);

  // Client-side tab + category filtering
  const filteredEmails = useMemo(
    () => filterInboxEmails(emails, unmappedEmails, activeTab, category, showResolved),
    [emails, unmappedEmails, activeTab, category, showResolved],
  );

  // Mark an email as read in local state + backend, refresh stats
  const markEmailAsRead = useCallback(
    async (emailId: string) => {
      // Optimistically update local state immediately (both lists: an
      // unmapped row opens from its own tab and must turn read too)
      setEmails((prev) =>
        prev.map((e) =>
          e.id === emailId ? { ...e, is_unread: false } : e,
        ),
      );
      setUnmappedEmails((prev) =>
        prev.map((e) =>
          e.id === emailId ? { ...e, is_unread: false } : e,
        ),
      );
      portalService.invalidateStatsCache();
      fetchStats();
      // Fire-and-forget backend call
      portalService.toggleReadStatus(emailId, true).catch((err) => {
        console.error('Failed to mark email as read:', err);
      });
    },
    [fetchStats],
  );

  // Mark a single email as unread in local state + backend, refresh stats
  const markEmailAsUnread = useCallback(
    async (emailId: string) => {
      // Optimistically update local state immediately
      setEmails((prev) =>
        prev.map((e) =>
          e.id === emailId ? { ...e, is_unread: true } : e,
        ),
      );
      setUnmappedEmails((prev) =>
        prev.map((e) =>
          e.id === emailId ? { ...e, is_unread: true } : e,
        ),
      );
      portalService.invalidateStatsCache();
      fetchStats();
      // Fire-and-forget backend call
      portalService.toggleReadStatus(emailId, false).catch((err) => {
        console.error('Failed to mark email as unread:', err);
      });
    },
    [fetchStats],
  );

  // Opening an email marks it read, like a normal mail client. This is the
  // 2026-10-15 revert of the 09-24 "never on open" rule (kanban a2888ff4,
  // reverting cda64d0f): the desk was clearing the same email in the
  // personal inbox and again in All Emails. Both views share one row (the
  // Gmail UNREAD label), so reading here shows read there. Mark as unread
  // is still one click away and sticks while the email stays open: only a
  // fresh selection re-marks.
  const markReadOnOpen = useCallback(
    (emailId: string) => {
      const email =
        emails.find((e) => e.id === emailId) ??
        unmappedEmails.find((e) => e.id === emailId);
      if (email?.is_unread) {
        markEmailAsRead(emailId);
      }
    },
    [emails, unmappedEmails, markEmailAsRead],
  );

  const handleEmailSelect = useCallback(
    (emailId: string) => {
      trackEvent('click', 'email_open', { email_id: emailId });
      setSelectedEmailId(emailId);
      markReadOnOpen(emailId);
    },
    [markReadOnOpen],
  );

  // Next / previous while an email is open (kanban 97abd058): moves through
  // the list as it is shown (tab, category, sort, search), fetching the next
  // page when Next is pressed on the last loaded row. Selecting this way is
  // the same as a list click, so it marks the email read the same way; it
  // records its own action (email_next / email_prev, not email_open) so the
  // usage graph can measure how often the run-of-opens pattern goes through it.
  const navigableIds = useMemo(() => filteredEmails.map((e) => e.id), [filteredEmails]);
  const canLoadMore = activeTab !== 'unmapped' && emails.length < totalEmails;
  const handleNavigate = useCallback(
    (target: NavigationTarget) => {
      trackEvent('click', `email_${target.direction}`, {
        email_id: target.id,
        from_email_id: target.fromId,
        via: target.via,
        index: target.index,
        loaded_more: target.loadedMore,
      });
      setSelectedEmailId(target.id);
      markReadOnOpen(target.id);
      if (searchParams.has('emailId')) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('emailId', target.id);
          return next;
        }, { replace: true });
      }
    },
    [searchParams, setSearchParams, markReadOnOpen],
  );
  const emailNavigation = useListNavigation({
    ids: navigableIds,
    currentId: selectedEmailId,
    total: activeTab === 'unmapped' ? totalUnmapped : totalEmails,
    hasMore: canLoadMore,
    loadingMore,
    loadMore: handleLoadMore,
    onNavigate: handleNavigate,
  });

  // Bulk action handlers
  const handleBulkMarkRead = useCallback(async () => {
    trackEvent('action', 'bulk_mark_read', { count: checkedIds.size });
    const ids = Array.from(checkedIds);
    // Optimistic update
    setEmails((prev) =>
      prev.map((e) => (checkedIds.has(e.id) ? { ...e, is_unread: false } : e)),
    );
    setCheckedIds(new Set());
    portalService.invalidateStatsCache();
    fetchStats();
    // Fire backend calls in parallel
    const results = await Promise.allSettled(
      ids.map((id) => portalService.toggleReadStatus(id, true)),
    );
    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length > 0) {
      addToast(`Failed to mark ${failures.length} email(s) as read.`, 'error');
      fetchEmails();
    }
  }, [checkedIds, fetchStats, fetchEmails, addToast]);

  const handleBulkMarkUnread = useCallback(async () => {
    trackEvent('action', 'bulk_mark_unread', { count: checkedIds.size });
    const ids = Array.from(checkedIds);
    setEmails((prev) =>
      prev.map((e) => (checkedIds.has(e.id) ? { ...e, is_unread: true } : e)),
    );
    setCheckedIds(new Set());
    portalService.invalidateStatsCache();
    fetchStats();
    const results = await Promise.allSettled(
      ids.map((id) => portalService.toggleReadStatus(id, false)),
    );
    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length > 0) {
      addToast(`Failed to mark ${failures.length} email(s) as unread.`, 'error');
      fetchEmails();
    }
  }, [checkedIds, fetchStats, fetchEmails, addToast]);

  const performBulkTrash = useCallback(async (
    ids: string[],
    confirmGuarded: boolean,
  ) => {
    const idSet = new Set(ids);
    setBulkTrashing(true);
    // Snapshot current state for rollback
    let previousEmails: PortalEmailItem[] = [];
    let previousUnmapped: PortalEmailItem[] = [];
    setEmails((prev) => { previousEmails = prev; return prev; });
    setUnmappedEmails((prev) => { previousUnmapped = prev; return prev; });
    try {
      // Optimistic: remove trashed emails from local state
      setEmails((prev) => prev.filter((e) => !idSet.has(e.id)));
      setUnmappedEmails((prev) => prev.filter((e) => !idSet.has(e.id)));
      setCheckedIds(new Set());
      await portalService.bulkMarkAsTrash(ids, { confirmGuarded });
      portalService.invalidateStatsCache();
      fetchStats();
      fetchEmails();
      if (unmappedLoadedRef.current) {
        fetchUnmappedEmails();
      }
    } catch (err) {
      // Rollback optimistic updates on failure
      setEmails(previousEmails);
      setUnmappedEmails(previousUnmapped);
      setCheckedIds(idSet);
      // Server trash guard: the selection contains order/Important emails.
      // Show the confirm dialog listing them instead of an error toast.
      const guarded = parseTrashGuardError(err);
      if (guarded) {
        setTrashGuard({ guarded, pendingIds: ids });
      } else {
        addToast('Failed to trash selected emails. Please try again.', 'error');
      }
    } finally {
      setBulkTrashing(false);
    }
  }, [fetchStats, fetchEmails, fetchUnmappedEmails, addToast]);

  const handleBulkTrash = useCallback(() => {
    trackEvent('action', 'bulk_trash', { count: checkedIds.size });
    void performBulkTrash(Array.from(checkedIds), false);
  }, [checkedIds, performBulkTrash]);

  const handleBulkMarkResolved = useCallback(async () => {
    trackEvent('action', 'bulk_resolve', { count: checkedIds.size });
    const ids = Array.from(checkedIds);
    const previousCheckedIds = new Set(checkedIds);
    setBulkResolving(true);
    let previousEmails: PortalEmailItem[] = [];
    setEmails((prev) => { previousEmails = prev; return prev; });
    try {
      // Optimistic update: mark selected emails as dismissed AND resolved.
      // Both flags are kept in sync so the unified is_resolved filter on the
      // Important tab picks up the change immediately, before the server-side
      // refetch.
      setEmails((prev) =>
        prev.map((e) =>
          previousCheckedIds.has(e.id)
            ? { ...e, notification_is_dismissed: true, is_resolved: true }
            : e,
        ),
      );
      setCheckedIds(new Set());
      await portalService.bulkDismissActionItems(ids);
      portalService.invalidateStatsCache();
      fetchStats();
    } catch {
      setEmails(previousEmails);
      setCheckedIds(previousCheckedIds);
      addToast('Failed to resolve selected emails. Please try again.', 'error');
    } finally {
      setBulkResolving(false);
    }
  }, [checkedIds, fetchStats, addToast]);

  const handleCheckboxChange = useCallback(
    (emailId: string, checked: boolean) => {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(emailId);
        } else {
          next.delete(emailId);
        }
        return next;
      });
    },
    [],
  );

  const selectedEmail =
    emails.find((e) => e.id === selectedEmailId) ??
    unmappedEmails.find((e) => e.id === selectedEmailId);
  const mappedSelected = selectedEmail ? mapEmailToComponent(selectedEmail) : null;

  const unmappedCount = resolveUnmappedCount(
    unmappedLoadedRef.current,
    totalUnmapped,
    alertsData,
  );

  // Important count: prefer the backend stat (accurate for thread-grouped
  // data even when not all pages are loaded); fall back to client-side
  // count from the loaded email list.
  const importantCount =
    stats?.important ??
    emails.filter(
      (e) =>
        e.notification_decision === 'notify' ||
        e.notification_decision === 'unsure',
    ).length;

  const allImportantCount = resolveAllImportantCount(
    stats?.all_important,
    emails,
  );

  const tabs = buildInboxTabs({
    importantCount,
    allImportantCount,
    unmappedCount,
  });



  const handleAssign = async (data: {
    assignee: string;
    category: string;
    notes: string;
  }) => {
    if (!selectedEmailId) return;
    trackEvent('action', 'email_assign', { email_id: selectedEmailId });
    const sp = salespersons.find(
      (s) => s.salesperson_no === data.assignee || s.name === data.assignee,
    );

    try {
      await portalService.assignEmail(selectedEmailId, {
        salesperson_no: sp?.salesperson_no || data.assignee,
        salesperson_name: sp?.name || data.assignee,
        notes: data.notes || undefined,
      });
      setEmails((prev) =>
        prev.map((email) => {
          if (email.id === selectedEmailId) {
            return {
              ...email,
              is_assigned: true,
              assigned_to: sp?.name || data.assignee,
              status: 'assigned',
            };
          }
          return email;
        }),
      );
      setShowAssignModal(false);
      addToast('Email assigned successfully!', 'success');
      portalService.invalidateStatsCache();
      fetchStats();
    } catch (err) {
      console.error('Failed to assign email:', err);
      addToast('Failed to assign email', 'error');
    }
  };

  if (error) {
    return (
      <div className={styles.header}>
        <div className={styles.errorState}>
          <p>{error}</p>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => fetchEmails(false)}
            type="button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>My Emails</h1>
          <InboxViewDropdowns
            category={category}
            onCategoryChange={handleCategoryChange}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />
        </div>
        <div className={styles.actions}>
          <button
            className={`${styles.btn} ${styles.btnOutline}`}
            onClick={() => fetchEmails(false)}
            type="button"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="23 4 23 10 17 10"></polyline>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className={styles.searchBar}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search by subject, sender, customer, or content..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            className={styles.searchClear}
            onClick={() => setSearch('')}
            type="button"
            title="Clear search"
          >
            &times;
          </button>
        )}
      </div>

      <InboxAlertBanners
        onViewUnmapped={() => {
          setActiveTab('unmapped');
          if (!unmappedLoadedRef.current) {
            fetchUnmappedEmails();
          }
        }}
      />

      {/* What the quality desk is waiting on this person for (kanban 2a28d4c0). */}
      <QualityAsksCard title="Quality is waiting on you" surface="inbox" showQuestionsLink />

      <FilterTabs
        activeTab={activeTab}
        tabs={tabs}
        onTabChange={(tab) => {
          trackEvent('filter', 'inbox_tab', { tab, source: 'chosen' });
          if (isInboxTab(tab)) {
            rememberInboxTab(userId, tab);
          }
          setTabSource('chosen');
          setActiveTab(tab);
          if (tab === 'unmapped' && !unmappedLoadedRef.current) {
            fetchUnmappedEmails();
          }
        }}
      />

      {/* Says when the open tab came from memory, not a click (kanban 778fe5cf). */}
      {tabSource === 'restored' && (
        <div
          className={styles.tabRestoredNote}
          data-testid="inbox-tab-restored"
          data-tab={activeTab}
          role="status"
        >
          Opened on <strong>{tabs.find((t) => t.id === activeTab)?.label ?? activeTab}</strong>,
          remembered from your last visit.
        </div>
      )}

      {(activeTab === 'important' || activeTab === 'all-important') && (
        <div className={styles.importantSubFilterRow}>
          <ImportantSubFilter
            value={importantReadState}
            onChange={setImportantReadState}
            totalCount={
              activeTab === 'all-important'
                ? (stats?.all_important ?? allImportantCount)
                : (stats?.important ?? importantCount)
            }
            unreadCount={
              activeTab === 'all-important'
                ? (stats?.all_important_unread ?? 0)
                : (stats?.important_unread ?? 0)
            }
            readCount={
              activeTab === 'all-important'
                ? (stats?.all_important_read ?? 0)
                : (stats?.important_read ?? 0)
            }
          />
          <label className={styles.showResolvedToggle}>
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            Show resolved
          </label>
        </div>
      )}

      {checkedIds.size > 0 && (
        <InboxBulkBar
          count={checkedIds.size}
          onMarkRead={handleBulkMarkRead}
          onMarkUnread={handleBulkMarkUnread}
          onTrash={handleBulkTrash}
          trashing={bulkTrashing}
          onMarkResolved={
            activeTab === 'important' || activeTab === 'all-important'
              ? handleBulkMarkResolved
              : undefined
          }
          resolving={bulkResolving}
          onClear={() => setCheckedIds(new Set())}
        />
      )}

      <div className={styles.listContainer}>
        <div className={styles.list}>
          {(activeTab === 'unmapped' ? loadingUnmapped : loading) ? (
            <div className={styles.emptyState}>
              <p>Loading emails...</p>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className={styles.emptyState}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
              <p>
                {debouncedSearch
                  ? 'No emails match your search'
                  : activeTab === 'unmapped'
                    ? 'No unmapped emails — all emails are mapped to a salesperson'
                    : activeTab === 'all-important'
                      ? 'No important emails across the team right now'
                      : 'No important emails — nothing needs your attention right now'}
              </p>
            </div>
          ) : (
            <>
              {filteredEmails.map((email) => {
                const mapped = mapEmailToComponent(email);
                const isResolved = isEmailResolved(email);
                return (
                  <div
                    key={email.id}
                    className={isResolved ? styles.resolvedEmailWrapper : undefined}
                  >
                    {isResolved && (
                      <span className={styles.resolvedBadge} title="Action item resolved">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                    <EmailListItem
                      email={mapped}
                      isSelected={selectedEmailId === email.id}
                      isChecked={checkedIds.has(email.id)}
                      onClick={() => handleEmailSelect(email.id)}
                      onCheckboxChange={(checked) =>
                        handleCheckboxChange(email.id, checked)
                      }
                    />
                  </div>
                );
              })}
              {/* Pagination footer */}
              <div className={styles.paginationFooter}>
                {activeTab === 'unmapped' ? (
                  <span className={styles.paginationCount}>
                    Showing {unmappedEmails.length} of {totalUnmapped.toLocaleString()} unmapped
                  </span>
                ) : (
                  <>
                    <span className={styles.paginationCount}>
                      Showing {filteredEmails.length} of {totalEmails.toLocaleString()}
                    </span>
                    {emails.length < totalEmails && (
                      <button
                        className={`${styles.btn} ${styles.btnOutline} ${styles.loadMoreBtn}`}
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        type="button"
                      >
                        {loadingMore ? 'Loading...' : `Load More (${Math.min(PAGE_SIZE, totalEmails - emails.length)} more)`}
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {selectedEmailId && mappedSelected && (
        <EmailViewModal
          // Remount per email: the modal's own reset effect is keyed on
          // subject + sender, which two emails of one thread share, so a
          // Next within a thread would otherwise keep the previous body.
          key={selectedEmailId}
          email={mappedSelected}
          emailId={selectedEmailId}
          onClose={closeEmailViewer}
          navigation={emailNavigation}
          onOrderAction={(action, orderId) => {
            addToast(
              `Order ${orderId.slice(0, 8)}... ${action === 'submit' ? 'submitted to Sage' : action === 'cancel' ? 'cancelled' : action}`,
              action === 'cancel' ? 'warning' : 'success',
            );
            portalService.invalidateStatsCache();
            fetchStats();
          }}
          onReplyClick={(action) => {
            setComposeAction(action);
            setShowCompose(true);
          }}
          onEmailChanged={() => {
            portalService.invalidateStatsCache();
            fetchEmails();
            fetchStats();
            fetchAlerts();
            if (unmappedLoadedRef.current) {
              fetchUnmappedEmails();
            }
          }}
          onMarkUnread={markEmailAsUnread}
          onMarkRead={markEmailAsRead}
          isUnread={!!mappedSelected.isUnread}
          onToast={addToast}
        />
      )}

      <AssignModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onAssign={handleAssign}
        salespersons={salespersons}
      />

      {showCompose && (
        <EmailComposeModal
          emailId={selectedEmailId}
          action={composeAction}
          onClose={() => setShowCompose(false)}
          onSent={(isSandbox, sent) => {
            addToast(
              isSandbox
                ? 'Sandbox: email processed'
                : 'Email sent successfully!',
              isSandbox ? 'warning' : 'success',
            );
            // Replying is the other thing (besides Mark as read) that clears unread.
            if (selectedEmailId) markEmailAsRead(selectedEmailId);
            fetchEmails(false);
            // The desk answered the customer; is the open Merlin card settled?
            // (11 of 13 aging flags were desk-answered threads — AU02393.)
            const replied = emails.find((e) => e.id === selectedEmailId) ?? unmappedEmails.find((e) => e.id === selectedEmailId);
            const proposal = sent ? proposalToSettleAfterReply(replied, sent.action, sent.toAddresses) : null;
            if (proposal) setReplySettle({ proposal, replyText: sent?.bodyText ?? '' });
          }}
        />
      )}

      {replySettle && (
        <EditProposalReplySettlePrompt
          proposal={replySettle.proposal}
          replyText={replySettle.replyText}
          onDismissed={() => {
            setReplySettle(null);
            addToast(
              `Merlin change card on ${replySettle.proposal.sage_order_no ?? 'the order'} dismissed`,
              'success',
            );
            fetchEmails(false);
          }}
          onKeepOpen={() => setReplySettle(null)}
        />
      )}

      {trashGuard && (
        <TrashGuardModal
          guarded={trashGuard.guarded}
          totalSelected={trashGuard.pendingIds.length}
          busy={bulkTrashing}
          onCancel={() => setTrashGuard(null)}
          onTrashAnyway={() => {
            const ids = trashGuard.pendingIds;
            setTrashGuard(null);
            void performBulkTrash(ids, true);
          }}
          onSkipGuarded={() => {
            const { unguardedIds } = partitionGuarded(
              trashGuard.pendingIds,
              trashGuard.guarded,
            );
            setTrashGuard(null);
            if (unguardedIds.length > 0) {
              void performBulkTrash(unguardedIds, false);
            }
          }}
        />
      )}

      <Toast toasts={toasts} onRemove={removeToast} />

      <AdminViewAsPrompt />
    </>
  );
};

export default PortalInboxPage;
